import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce, useSize } from 'ahooks'
import { CommandLoading, Command as CommandPrimitive } from 'cmdk'
import { times } from 'lodash-es'

import { isDateField } from '@/lib/json-schema-utils.ts'
import { cn } from '@/lib/utils.ts'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group.tsx'
import { useFindAttributeGroups } from '@/features/attribute-groups/hooks/use-find-attribute-groups.ts'
import { useFindAttributeValues } from '@/features/attributes/hooks/use-find-attribute-values.ts'
import { useFindAttributes } from '@/features/attributes/hooks/use-find-attributes.ts'
import { DatePickerFilterInput } from '@/routes/_authenticated/documents/browse/-components/date-picker-filter-input.tsx'

interface SearchInputProps {
  onAttributeFilterChange?: (args: {
    attributeId: string
    value: unknown
  }) => void
  onAttributeGroupFilterChange?: (args: { attributeGroupId: string }) => void
  onTextFilterChange?: (text: string) => void
}

export const SearchInput: React.FC<SearchInputProps> = ({
  onTextFilterChange,
  onAttributeFilterChange,
  onAttributeGroupFilterChange,
}) => {
  const inputRef = useRef(null)
  const size = useSize(inputRef)
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [selectionStep, setSelectionStep] = useState<
    'filterSelection' | 'valueSelection'
  >('filterSelection')
  const [selection, setSelection] = useState<
    | { selectionType: 'attribute'; value: string }
    | { selectionType: 'attributeGroup' }
    | null
  >()

  const findAttributesQuery = useFindAttributes()
  const attributes = useMemo(() => {
    return findAttributesQuery?.data?.data?.filter((a) => {
      const isPrimitiveType = !['array', 'object'].includes(
        a.json_schema!.type!
      )
      const isNested = ['array', 'object'].includes(
        a.json_schema!.items?.type ?? a.json_schema!.properties?.type
      )
      return isPrimitiveType || !isNested
    })
  }, [findAttributesQuery?.data?.data])

  const handleAttributeSelected = (attributeId: string) => {
    // setSelectedAttributeId(attributeId)
    setSelection({ selectionType: 'attribute', value: attributeId })
    setSearchQuery('')
    setSelectionStep('valueSelection')
  }
  const handleAttributeValueSelection = ({
    attributeId,
    value,
  }: {
    attributeId: string
    value: unknown
  }) => {
    onAttributeFilterChange?.({ attributeId, value })
    setIsOpen(false)
  }

  const findAttributeGroupsQuery = useFindAttributeGroups()
  const attributeGroups = findAttributeGroupsQuery.data?.data

  const handleAttributeGroupSelection = (attributeGroupId: string) => {
    onAttributeGroupFilterChange?.({ attributeGroupId })
    setIsOpen(false)
  }

  // TODO
  const handleInputKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Enter' &&
      selectionStep === 'valueSelection' &&
      selection?.selectionType === 'attribute'
    ) {
      handleAttributeValueSelection({
        attributeId: selection?.value as string,
        value: searchQuery,
      })
    }
  }
  const handleTextSearchSelected = () => {
    onTextFilterChange?.(searchQuery)
    setSearchQuery('')
    setIsOpen(false)
  }
  const selectedAttribute = useMemo(
    () =>
      selection?.selectionType === 'attribute' && selection?.value
        ? attributes?.find((a) => a.id === selection?.value)
        : null,
    [
      attributes,
      selection?.selectionType,
      selection?.selectionType === 'attribute' ? selection?.value : null,
    ]
  )

  const valueSelectionMethod = useMemo(() => {
    if (selectedAttribute?.json_schema?.type === 'array') {
      return selectedAttribute?.json_schema?.items?.type
    }

    return selectedAttribute?.json_schema?.type
  }, [
    selectedAttribute?.json_schema?.items?.type,
    selectedAttribute?.json_schema?.type,
  ])

  const searchQueryDebounced = useDebounce(searchQuery, { maxWait: 500 })

  const findAttributeValuesQuery = useFindAttributeValues(
    {
      attributeId:
        selection?.selectionType === 'attribute' ? selection.value : '',
      search: searchQuery === '' ? searchQuery : searchQueryDebounced,
    },
    {
      enabled:
        valueSelectionMethod === 'string' &&
        selection?.selectionType === 'attribute',
    }
  )
  const attributeValues = findAttributeValuesQuery?.data?.data

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      // setSelectedAttributeId(null)
      setSelection(null)
      setSelectionStep('filterSelection')
    }
  }, [isOpen])

  const isLoadingItems =
    selectionStep === 'valueSelection' &&
    (findAttributeValuesQuery.isLoading || findAttributeGroupsQuery.isLoading)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="grow">
          <Input
            ref={inputRef}
            className={cn('pointer-events-none mr-2', isOpen && 'opacity-0')}
            type="text"
            placeholder="Inserisci il testo da ricercare o il nome di un attributo..."
            tabIndex={-1}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="relative -top-[45px] !animate-none p-0"
        style={{ width: size?.width }}
      >
        <Command shouldFilter={selectionStep === 'filterSelection'}>
          {/*<CommandInput*/}
          {/*    */}
          {/*  value={searchQuery}*/}
          {/*  onValueChange={setSearchQuery}*/}
          {/*  placeholder="Type a command or search..."*/}
          {/*/>*/}
          <div
            className={cn(
              'flex h-10 items-center px-3',
              selectionStep === 'filterSelection' ||
                (valueSelectionMethod === 'string' && 'border-b')
            )}
            cmdk-input-wrapper=""
          >
            {selectionStep === 'valueSelection' && (
              <span className="mr-2 h-7 text-nowrap rounded bg-gray-100 p-1 px-2 text-sm">
                {selection?.selectionType === 'attribute'
                  ? selectedAttribute?.name
                  : null}
                {selection?.selectionType === 'attributeGroup'
                  ? 'Gruppo Attributi'
                  : null}
              </span>
            )}

            {selectionStep === 'filterSelection' ||
            (valueSelectionMethod === 'string' &&
              !isDateField(selectedAttribute!.json_schema)) ||
            selection?.selectionType === 'attributeGroup' ? (
              <CommandPrimitive.Input
                className={cn(
                  'placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50'
                )}
                value={searchQuery}
                onValueChange={setSearchQuery}
                onKeyDown={handleInputKeydown}
                placeholder="Inserisci il testo da ricercare o il nome di un attributo..."
              />
            ) : null}

            {selectionStep === 'valueSelection' &&
              valueSelectionMethod === 'boolean' && (
                <ToggleGroup type="single">
                  <ToggleGroupItem
                    role="option"
                    className="h-7"
                    value=""
                    onClick={() =>
                      handleAttributeValueSelection({
                        attributeId: selectedAttribute!.id,
                        value: true,
                      })
                    }
                  >
                    Si
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    role="option"
                    className="h-8"
                    value=""
                    onClick={() =>
                      handleAttributeValueSelection({
                        attributeId: selectedAttribute!.id,
                        value: false,
                      })
                    }
                  >
                    No
                  </ToggleGroupItem>
                </ToggleGroup>
              )}

            {selectionStep === 'valueSelection' &&
              valueSelectionMethod === 'string' &&
              isDateField(selectedAttribute!.json_schema) && (
                <DatePickerFilterInput
                  onSelect={(dateRange) =>
                    handleAttributeValueSelection({
                      attributeId: selectedAttribute!.id,
                      value: dateRange,
                    })
                  }
                />
              )}
          </div>

          {selectionStep === 'filterSelection' ||
          (valueSelectionMethod === 'string' &&
            !isDateField(selectedAttribute!.json_schema)) ? (
            <CommandList className="max-h-[500px]">
              <CommandGroup>
                {attributeValues?.map((aValue) => (
                  <CommandItem
                    key={aValue}
                    onSelect={() =>
                      handleAttributeValueSelection({
                        attributeId: selectedAttribute!.id,
                        value: aValue,
                      })
                    }
                  >
                    {/*<Calendar className="mr-2" />*/}
                    <span>{aValue}</span>
                  </CommandItem>
                ))}
              </CommandGroup>

              {searchQuery && !selection && (
                <>
                  <CommandGroup forceMount>
                    <CommandItem
                      onSelect={() => handleTextSearchSelected()}
                      forceMount
                    >
                      Ricerca testo: {searchQuery}
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}

              {selectionStep === 'filterSelection' && (
                <>
                  <CommandGroup heading="Attributi">
                    {attributes?.map((attribute) => (
                      <CommandItem
                        key={attribute.id}
                        onSelect={() => handleAttributeSelected(attribute.id)}
                      >
                        {/*<Calendar className="mr-2" />*/}
                        <span>{attribute.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>

                  <CommandSeparator />

                  {/*<CommandItem onSelect={() => handleAttributeGroupSelected()}>*/}
                  {/*  /!*<Calendar className="mr-2" />*!/*/}
                  {/*  Gruppo Attributi*/}
                  {/*</CommandItem>*/}
                  <CommandGroup heading="Gruppo Attributi">
                    {attributeGroups?.map((attributeGroup) => (
                      <CommandItem
                        key={attributeGroup.id}
                        onSelect={() =>
                          handleAttributeGroupSelection(attributeGroup.id)
                        }
                      >
                        {/*<Calendar className="mr-2" />*/}
                        <span>{attributeGroup.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {isLoadingItems && (
                <CommandLoading>
                  <CommandGroup>
                    {times(10).map((index) => (
                      <CommandItem key={index}>
                        <Skeleton className="h-2 w-1/3 md:w-1/6" />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandLoading>
              )}

              {selectionStep !== 'filterSelection' && (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
            </CommandList>
          ) : null}
        </Command>

        {/*<div className="border-t bg-gray-100" cmdk-raycast-footer=""></div>*/}
      </PopoverContent>
    </Popover>
  )
}
